/**
 * PromptBlock — prompt text display with optional copy icon.
 *
 * Figma source: Photo viewer `40000026:11835`.
 * - Font: Raleway SemiBold 14sp #fff (text-primary).
 * - Max 3 lines, internal scroll on overflow.
 * - Copy icon 24×24 visible ONLY when userPrompt is non-empty.
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Icon } from '../../primitives';

export default function PromptBlock({ userPrompt, onCopy }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timeout = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  if (!userPrompt) return null;

  const handleCopy = async () => {
    await onCopy?.();
    setCopied(true);
  };

  return (
    <div className="flex items-start gap-2 py-0">
      {/* Prompt text: up to 3 lines, overflow scroll */}
      <p
        className="flex-1 text-text-primary font-sans font-semibold"
        style={{
          fontSize: 14,
          lineHeight: '20px',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {userPrompt}
      </p>

      {/* Copy icon — only shown when userPrompt is non-empty */}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={t('viewer.copyPrompt')}
        className={copied ? 'flex-shrink-0 text-text-success' : 'flex-shrink-0 text-text-secondary'}
        style={{ width: 24, height: 24 }}
        data-testid="copy-prompt-icon"
      >
        <Icon
          name={copied ? 'check' : 'copy'}
          size={24}
          className={copied ? 'text-text-success' : 'text-text-secondary'}
        />
      </button>
    </div>
  );
}
